import { DotLottie } from "https://cdn.jsdelivr.net/npm/@lottiefiles/dotlottie-web/+esm";

// Global reference tracking the single running animation instance
let activeLottieInstance = null;
let player = null;

// =====================================
// INITIALISE SLIDE AND EVENTS
// =====================================

const carousel = new Splide('.splide', {
  type: 'fade',
  pagination: false,
  arrows: false,
  drag: false
});

carousel.on('mounted', () => {
  handleCharacterSelectionSlideInteractions(carousel.Components.Slides.getAt(carousel.index).slide);
});

carousel.on('move', (newIndex, prevIndex) => {

  // Destroy any youtube player before moving to next slide
  if (player) {
    player.destroy();
  }

  // Immediately destroy the old instance to prevent memory leaks
  if (activeLottieInstance) {
    activeLottieInstance.destroy();
    activeLottieInstance = null;
  }

  // If one of the previous slide is a level slide, reset level content to default
  if (prevIndex >= 2 && prevIndex <= 14) {
    const currentSlide = carousel.Components.Slides.getAt(prevIndex).slide;
    resetLevel(currentSlide);
  }

  const nextSlide = carousel.Components.Slides.getAt(newIndex).slide;

  if (newIndex == 0) return handleCharacterSelectionSlideInteractions(nextSlide);

  // Slide 1 - 14 are level slides
  if (newIndex >= 1 && newIndex <= 14) return handleLevelSlideInteractions(nextSlide);

});

carousel.mount();

// =====================================
// JUMP TO SLIDE BUTTONS
// =====================================

const goToButtons = document.querySelectorAll('.go-to-btn');

goToButtons.forEach(button => {
  button.onclick = () => {
    // Update stage in sessionStorage
    sessionStorage.setItem('kotm_stage', button.getAttribute('data-stage'));
    const slideNum = button.getAttribute('data-slide');
    if (slideNum) {
      carousel.go(parseInt(slideNum));
    } else {
      // Go to slide 1: Character Selection
      carousel.go(0);
    }
  }  
});

const nextLevelButtons = document.querySelectorAll('.next-level-btn');

nextLevelButtons.forEach(button => {
  button.onclick = () => carousel.go('>');
});

// =====================================
// HELPER FUNCTIONS
// =====================================

function handleCharacterSelectionSlideInteractions(activeSlideElement) {

  const selectCharacterRedBtn = activeSlideElement.querySelector('.selectCharacterRedBtn');
  const selectCharacterBlueBtn = activeSlideElement.querySelector('.selectCharacterBlueBtn');

  // Store character selection in browser sessionStorage kotm stands for Kidsafe On The Move

  selectCharacterRedBtn.onclick = () => {
    sessionStorage.setItem('kotm_character', 'red');
    goToNextStage();
  };  

  selectCharacterBlueBtn.onclick = () => {
    sessionStorage.setItem('kotm_character', 'blue');
    goToNextStage();
  };  

}

function goToNextStage() {

  const stage = sessionStorage.getItem('kotm_stage');

  switch (stage) {
    case 'regional':
      return carousel.go(4);
    case 'vehicle_safety':
      return carousel.go(8);
    case 'riding_and_device':
      return carousel.go(12);
    default:
      return carousel.go(1); // Default goes to Metro
  }

}

// Core function to swap animations safely
function handleLevelSlideInteractions(activeSlideElement) {
  
  let selectedCharacter = sessionStorage.getItem('kotm_character') == 'blue' ? 'blue' : 'red';

  // Show selected character related assets
  showElements(activeSlideElement, `.character-${selectedCharacter}`);

  const canvas = activeSlideElement.querySelector('.lottie-canvas');
  // Concat "-red.dottie" or "-blue-lottie" the part url
  const lottieUrl = canvas.getAttribute('data-lottie-part-url') + `-${selectedCharacter}.lottie`;

  // Find the dialog and button elements local to this slide
  const introDialog = activeSlideElement.querySelector('.intro-dialog');
  const videoDialog = activeSlideElement.querySelector('.video-dialog');
  const videoPlaceholder = activeSlideElement.querySelector('.video-placeholder');
  const firstQuestionDialog = activeSlideElement.querySelector('.question-dialog'); // Get the first question dialog in DOM
  const videoBtn = activeSlideElement.querySelector('.video-btn');
  const startQuizBtn = activeSlideElement.querySelector('.start-quiz-btn');
  const submitQuestionBtns = activeSlideElement.querySelectorAll('.submit-question-btn');
  const progressScreen = activeSlideElement.querySelector('.progress-screen');

  // 2. Initialize the fresh animation on the active canvas
  activeLottieInstance = new DotLottie({
    autoplay: true,
    loop: false,
    canvas: canvas,
    src: lottieUrl,
    layout: {
      fit: 'cover',
      align: [0.5, 1] // pin to bottom center
    },
    renderConfig: {
      autoResize: true, // deal with screen being resize, it re-renders to the correct ratio
      devicePixelRatio: window.devicePixelRatio
    }
  });

  // 4. Listen for completion -> open the dialog box smoothly
  activeLottieInstance.addEventListener('complete', () => {
    if (introDialog) {
      introDialog.showModal(); // Native browser method triggers [open] CSS state
    }
  });

  videoBtn.onclick = () => {
    introDialog.close(); // Closes the box and hides it smoothly via CSS transitions
    // Wait for the dialog's CSS transition (400ms) to complete before sliding
    setTimeout(() => {
      startQuizBtn.disabled = true;
      videoDialog.showModal();    
      player = new YT.Player(videoPlaceholder, {
        height: '360',
        width: '640',
        videoId: videoDialog.getAttribute('data-video-id'),
        playerVars: {
          'playsinline': 1, // Required for iOS to play video inline instead of fullscreen 
          'controls': isDebug() ? 1 : 0, // Hides the progress bar and player controls
          'rel': 0 // Shows related video from the same channel. If not specify, it will show random videos
        },
        events: {
          'onStateChange': (event) => {
            if (event.data == YT.PlayerState.ENDED) {
              startQuizBtn.disabled = false;
            }
          }
        }
      });
    }, 400); 
  };

  startQuizBtn.onclick = () => {
    videoDialog.close();
    setTimeout(() => {
      firstQuestionDialog.showModal(); 
      // Destory Youtube player
      player.destroy();
    }, 400); 
  };

  submitQuestionBtns.forEach(button => {

    button.onclick = (event) => {

      const currentQuestionDialog = event.target.closest('.question-dialog');

      // Hide all notices
      hideElements(currentQuestionDialog, '.notice');

      // check if any answers selected?
      const answersSelected = currentQuestionDialog.querySelectorAll('input[type="radio"]:checked');

      if (answersSelected.length == 0) {
        currentQuestionDialog.querySelector('.no-answers').style.display = 'block';     
        return;
      }

      const correctAnswerSelected = currentQuestionDialog.querySelector('.correct-answer > input:checked');

      if (!correctAnswerSelected) {
        currentQuestionDialog.querySelector('.incorrect-answers').style.display = 'block';
        currentQuestionDialog.querySelector('.correct-answer').classList.add('highlight');
        return;
      }

      // Answer is correct, let's move to next question
      currentQuestionDialog.close();

      setTimeout(() => {
        const nextQuestionDialog = currentQuestionDialog.nextElementSibling;
        if (nextQuestionDialog) {
          nextQuestionDialog.showModal();
        } else {
          // Show process screen
          progressScreen.style.display = 'block';
          setTimeout(() => {
            // Add .active class to animate the progress bar
            progressScreen.querySelector('.progress-bar').classList.add('active');
          }, 400);
        }
      }, 400); 

    };

  });

  // Get Download Certficate button and dialog
  const downloadCertBtn = activeSlideElement.querySelector('.open-download-cert-btn');
  const downloadCertDialog = activeSlideElement.querySelector('.download-cert-dialog');

  if (downloadCertBtn) downloadCertBtn.onclick = () => downloadCertDialog.showModal();

}

function resetLevel(slide) {

  const canvas = slide.querySelector('.lottie-canvas');
  const ctx = canvas.getContext('2d');

  // Empty the canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  
  // Close any lingering dialogs
  slide.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());

  // Hide Progress Screen
  const progressScreen = slide.querySelector('.progress-screen');
  progressScreen.style.display = 'none';
  // Remove active progress bar
  progressScreen.querySelector('.progress-bar').classList.remove('active');

  // Reset forms
  const forms = slide.querySelectorAll('.form');
  forms.forEach(element => element.reset()); 

  // Remove hint from correct answers
  const hints = slide.querySelectorAll('.hint');
  hints.forEach(element => element.classList.remove('highlight')); 

  // Hide all notices
  hideElements(slide, '.notice');

  // Reset all character related assets
  hideElements(slide, '.character-red');
  hideElements(slide, '.character-blue');

}

function hideElements(slide, className) {
  const elements = slide.querySelectorAll(className);
  elements.forEach(element => element.style.display = 'none');
}

function showElements(slide, className) {
  const elements = slide.querySelectorAll(className);
  elements.forEach(element => element.style.display = 'block');
}

function isDebug() {
  if (document.querySelector('#menu')) return true;
  return false;
}

// document.addEventListener("DOMContentLoaded", function() {
//   const certForm = document.getElementById("certForm");
//   if (certForm) {
//     // Switch execution behavior if screen size matches mobile/tablet layout viewports
//     if (window.matchMedia("(max-width: 768px)").matches) {
//       certForm.removeAttribute("target"); // Downloads via standard _self workflow
//     }
//   }
// });
